import React, { useEffect, useState } from "react";
import { Button, Divider, Flex, Form } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import api from "../../../api";
import { useToast } from "../../../components/toast/toast";
import { useBreadcrumb } from "../../../store/context/BreadcrumbContext";
import CustomHeader from "../../../components/header/CustomHeader";
import Loader from "../../../components/spin/Loader";
import { formatCurrency } from "../../../utilities/utils";
import { LotteryVisibilityActions } from "../utils/LotteryHeaderActions";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import LotteryTabs from "./tabs/LotteryTabs";
import CustomModal from "../../../components/modal/CustomModal";
import CustomInput from "../../../components/input/CustomInput";
import GiveLottery from "./components/GiveLottery";
import { IconDownload } from "@tabler/icons-react";
import { downloadProgressCSV } from "./components/DownloadProgress";

const LotteryDetail = () => {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [form] = Form.useForm();
  const { setBreadcrumb } = useBreadcrumb();
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const [draft, setDraft] = useState(false);
  const [showModalGen, setShowModalGen] = useState(false);
  const [showModalNum, setShowModalNum] = useState(false);
  const [trigger, setTrigger] = useState(true);
  const [activeKey, setActiveKey] = useState("1");
  const [thumbnail, setThumbnail] = useState("");
  const [gallery, setGallery] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);

  const [editorData, setEditorData] = useState({
    content: "",
    info: "",
    howto: "",
    agreement: "",
  });
  const [awardData, setAwardData] = useState({
    orderNum: 0,
    prize: 0,
    title: "",
    decsription: "",
  });

  const awardHandler = (key, value) => {
    setAwardData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    const fetchLottery = async () => {
      const res = await api.get_lottery_by_id(id);
      if (res.status === 200 && res.data?.data) {
        const data = res.data.data;

        // console.log("fetch data -->", data);
        setSummary(data);
        setThumbnail(data.images?.thumbnail || "");
        setGallery(
          Array.isArray(data.images?.gallery) ? data.images.gallery : [],
        );

        form.setFieldsValue({
          ...data,
          draw_date: data.draw_date ? dayjs(data.draw_date) : null,
          sms: data.config?.sms,
          ticket_limit: data.config?.ticket_limit,
          ignore_triple: data.config?.ignore_triple,
          bank_number: data.config?.bank_number,
          live_link: data.config?.live_link,
        });
        setEditorData(data?.content ? JSON.parse(data?.content) : {});

        // console.log("addBread --->", data);
        setBreadcrumb([
          { title: "Дашбоард", path: "/" },
          {
            title:
              type === "active"
                ? "Идэвхтэй сугалаа"
                : type === "draft"
                  ? "Draft сугалаа"
                  : "Дууссан сугалаа",
            path: `/lottery/${type}`,
          },
          { title: data.ticket_prefix, path: `/lottery/${type}/${id}/edit` },
        ]);
        setTrigger(false);
      } else {
        toast(res.data.message || "Сугалаа олдсонгүй", "error");
      }
      setInitialLoading(false);
    };

    fetchLottery();
  }, [id, form, trigger]);

  const onFinish = async (values) => {
    // setIsLoading(true);

    const payload = {
      title: values.title,
      slug: values.slug,
      ticket_prefix: values.ticket_prefix,
      ticket_price: values.ticket_price,
      ticket_qty: values.ticket_qty,
      draw_date: values.draw_date,
      content: values.content,
      // images: values.images?.map((file) => ({
      //   name: file.name,
      //   url: file.url || file.response?.url,
      // })),
      // images: Array.isArray(values.images) ? values.images : [values.images],
      images: values.images || undefined,
      config: {
        ignore_triple: values.ignore_triple || false,
        ticket_limit: values.ticket_limit,
        sms: values.sms,
        bank_number: values.bank_number,
        live_link: values.live_link,
      },
    };

    try {
      const res = await api.update_lottery(id, payload);
      if (res.status === 200) {
        toast("Амжилттай хадгалагдлаа!", "success");
        setTrigger(true);
      } else if (res.status === 403) {
        // console.log("error", res);
        toast(res?.data?.message, "warning");
      } else {
        toast(res?.data?.message || "Хадгалах үед алдаа гарлаа", "error");
      }
    } catch (err) {
      // console.error("Update error:", err);
      toast("Системийн алдаа", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const changeState = async (value) => {
    const res = await api.update_lottery_state(id, {
      visibility: value?.visibility,
      status: value?.status,
    });
    // console.log("changeState res --->", res);
    if (res.status === 200) {
      toast("Амжилттай хадгаллаа.", "success");
      if (value.visibility === "public") {
        navigate("/lottery/active");
      } else {
        navigate("/lottery/draft");
      }
    } else {
      toast(res.data?.message || "Алдаа гарлаа", "error");
    }
    setVisible(false);
  };

  const generateNumbers = async () => {
    setIsLoading(true);
    const res = await api.generate_numbers(id, {});
    // console.log("generateNumbers res --->", res);
    if (res.status === 201) {
      setTrigger(true);
      toast("Сугалааны дугаарууд үүсгэгдлээ", "success");
    } else {
      toast(res.data?.message || "Алдаа гарлаа", "error");
    }
    setIsLoading(false);
    setShowModalGen(false);
  };

  const drawHandler = async () => {
    const res = await api.create_award({
      lottery_id: summary?.id,
      title: awardData.title,
      prize_amount: awardData.prize || 0,
    });
    // console.log("create draw res--->", res);
    if (res.status === 201) {
      toast("Амжилттай үүслээ.", "success");
      setShowModal(false);
      setActiveKey("2");
      setAwardData({
        orderNum: 0,
        prize: 0,
        title: "",
        decsription: "",
      });
    } else {
      toast("Алдаа гарлаа", "error");
    }
  };

  const getLotteryProgress = async () => {
    setLoadingReport(true);
    const res = await api.lottery_progress({ lottery_id: id });
    console.log("lottery_process res --->", res);
    if (res?.status === 200) {
      downloadProgressCSV(res.data);
      toast("Сугалааны тайлан татагдлаа", "success");
    } else {
      toast(res.data?.message || "Алдаа гарлаа", "error");
    }
    setLoadingReport(false);
  };

  if (initialLoading) return <Loader />;

  return (
    <Flex vertical gap={"10px"}>
      <CustomHeader
        title={summary?.title || "Сугалаа засах"}
        visibility={summary?.visibility || ""}
        status={summary?.status || ""}
        leftSide={
          <Flex vertical gap={"4px"}>
            <p style={{ lineHeight: "20px" }}>
              Сугалааны дугаар: {summary?.ticket_prefix}
            </p>
            <p>Сугалааны үнэ: {formatCurrency(summary?.ticket_price)}</p>
          </Flex>
        }
        rightSide={
          <Flex vertical align="flex-end" gap={"10px"}>
            {summary?.status !== "completed" && (
              <Flex gap={"10px"}>
                <Button
                  // type="dashed"
                  style={{ padding: "0px 20px" }}
                  onClick={() => setShowModal(true)}
                  // onClick={() => setActiveKey("2")}
                >
                  Шагнал үүсгэх
                </Button>
                {summary && !summary?.has_numbers && (
                  <Button
                    style={{ padding: "0px 20px" }}
                    onClick={() => setShowModalGen(true)}
                    disabled={isLoading}
                    loading={isLoading}
                  >
                    Сугалааны дугаар үүсгэх
                  </Button>
                )}

                {summary && summary?.has_numbers && (
                  <GiveLottery data={summary} />
                )}

                <LotteryVisibilityActions
                  hasNumbers={summary?.has_numbers}
                  visibility={summary?.visibility}
                  status={summary?.status}
                  setData={setVisible}
                  setDraft={setDraft}
                />
              </Flex>
            )}
            <Button
              variant="dashed"
              color="magenta"
              style={{ padding: "0px 20px" }}
              onClick={() => getLotteryProgress()}
              disabled={loadingReport}
              loading={loadingReport}
            >
              <IconDownload size={18} strokeWidth={1.5} />
              Тайлан татах
            </Button>
            <Button
              type="link"
              target="_blank"
              href={`${process.env.REACT_APP_SELECTION_URL}/id/${id}`}
              // href={`https://beta-selection.grandlotto.mn/id/${id}`}
              // href={`http://localhost:3002/id/${id}`}
            >
              Шалгаруулалтын линк
            </Button>
          </Flex>
        }
      />
      <Divider />

      <LotteryTabs
        lotteryId={id}
        form={form}
        onFinish={onFinish}
        isLoading={isLoading}
        editorData={editorData}
        setEditorData={setEditorData}
        trigger={trigger}
        activeKey={activeKey}
        setActiveKey={setActiveKey}
        thumbnail={thumbnail}
        setThumbnail={setThumbnail}
        gallery={gallery}
        setGallery={setGallery}
      />

      {/* --- Create award --- */}
      <CustomModal
        title={"Шагнал үүсгэх"}
        open={showModal}
        onOk={drawHandler}
        onCancel={() => {
          setAwardData({
            orderNum: 0,
            prize: 0,
            title: "",
            decsription: "",
          });
          setShowModal(false);
        }}
        disabled={!awardData.title}
      >
        <Flex vertical gap={"10px"}>
          {/* <CustomInput
            title={"Шагналын зэрэглэл"}
            required
            type="inputNumber"
            value={awardData.orderNum}
            // onChange={(e) => awardHandler("orderNum", e.target.value)}
            onChange={(val) => awardHandler("orderNum", val)}
          /> */}

          <CustomInput
            title={"Гарчиг"}
            required
            value={awardData.title}
            onChange={(e) => awardHandler("title", e.target.value)}
          />
          <CustomInput
            title={"Үнийн дүн"}
            value={awardData.prize}
            onChange={(e) => awardHandler("prize", e.target.value)}
          />

          {/* <CustomInput
            title={"Дэлгэрэнгүй"}
            value={awardData.decsription}
            onChange={(e) => awardHandler("decsription", e.target.value)}
          /> */}
        </Flex>
      </CustomModal>
      {/* --- Change visibility and status /draft/ --- */}
      <ConfirmModal
        showModal={draft}
        variant="warning"
        title="Итгэлтэй байна уу?"
        description={`"Draft" төлөвт шилжиж хэрэглэгчид харагдахгүй болохыг анхаарна уу.`}
        setShowModal={setDraft}
        cancelHandler={() => setDraft(false)}
        submitHandler={() =>
          changeState({
            visibility: "draft",
            status: summary?.status,
          })
        }
      />

      {/* --- Change visibility and status /draft -> public/ + /upcoming -> ongoing/ --- */}
      <CustomModal
        title={"Нийтлэх төлөвөө сонгоно уу."}
        open={visible}
        onCancel={() => setVisible(false)}
        footer={
          <Flex gap={"15px"}>
            <Button
              style={{ width: "100%", height: "40px" }}
              onClick={() => {
                changeState({
                  visibility: "public",
                  status: "upcoming",
                });
              }}
              // disabled={
              //   summary?.visibility === "public" ||
              //   (summary?.visibility === "draft" &&
              //     summary?.status === "ongoing")
              //     ? true
              //     : false
              // }
            >
              Тун удахгүй
            </Button>
            <Button
              type="primary"
              style={{ width: "100%", height: "40px" }}
              onClick={() => {
                changeState({
                  visibility: "public",
                  status: "ongoing",
                });
              }}
            >
              Идэвхтэй
            </Button>
          </Flex>
        }
      >
        <Flex vertical gap={"10px"}>
          <p>
            "<span style={{ color: "#0066cc" }}>Тун удахгүй</span>" товчийг
            дарснаар мэдээлэл нийтлэгдэх ч тасалбарын захиалга хаалттай хэвээр
            үлдэнэ.
          </p>
          <p>
            "<span style={{ color: "#0066cc" }}>Идэвхтэй</span>" товчийг
            дарснаар хэрэглэгчид тасалбар худалдан авах боломжтойгоор
            нийтлэгдэнэ.
          </p>
          <Divider />
        </Flex>
      </CustomModal>
      {/* --- Generate numbers --- */}
      <ConfirmModal
        showModal={showModalGen}
        variant="warning"
        title="Итгэлтэй байна уу?"
        description={
          "Анхааруулга: Энэ үйлдлийг хийснээр буцаах боломжгүй тул сайтар нягтална уу."
        }
        setShowModal={setShowModalGen}
        cancelHandler={() => setShowModalGen(false)}
        submitHandler={() => {
          generateNumbers();
        }}
      />
    </Flex>
  );
};

export default LotteryDetail;
